import { create } from 'zustand';

export type MessageVariant = 'info' | 'success' | 'warning' | 'error';
export type MessageType = 'toast' | 'alert' | 'confirm' | 'prompt';

export interface Message {
  id: number;
  type: MessageType;
  variant?: MessageVariant;
  title: string;
  content?: string;
  timeout?: number;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: (value?: any) => void;
  onCancel?: () => void;
  defaultValue?: string;
  placeholder?: string;
}

interface MessageState {
  messages: Message[];
  addMessage: (message: Omit<Message, 'id'>) => void;
  removeMessage: (id: number) => void;
  toast: (title: string, variant?: MessageVariant, timeout?: number) => void;
  alert: (title: string, content?: string, variant?: MessageVariant) => void;
  confirm: (title: string, content?: string, options?: {
    confirmText?: string;
    cancelText?: string;
    variant?: MessageVariant;
    onConfirm?: () => void;
    onCancel?: () => void;
  }) => void;
  prompt: (title: string, options?: {
    content?: string;
    defaultValue?: string;
    placeholder?: string;
    confirmText?: string;
    cancelText?: string;
    variant?: MessageVariant;
    onConfirm?: (value: string) => void;
    onCancel?: () => void;
  }) => void;
}

export const useMessageStore = create<MessageState>()((set) => ({
  messages: [],

  addMessage: (message) =>
    set((state) => ({
      messages: [...state.messages, { ...message, id: Date.now() }],
    })),

  removeMessage: (id) =>
    set((state) => ({
      messages: state.messages.filter((message) => message.id !== id),
    })),

  toast: (title, variant = 'info', timeout = 3000) =>
    set((state) => ({
      messages: [
        ...state.messages,
        {
          id: Date.now(),
          type: 'toast',
          variant,
          title,
          timeout,
        },
      ],
    })),

  alert: (title, content, variant = 'info') =>
    set((state) => ({
      messages: [
        ...state.messages,
        {
          id: Date.now(),
          type: 'alert',
          variant,
          title,
          content,
        },
      ],
    })),

  confirm: (title, content, options = {}) =>
    set((state) => ({
      messages: [
        ...state.messages,
        {
          id: Date.now(),
          type: 'confirm',
          variant: options.variant || 'warning',
          title,
          content,
          confirmText: options.confirmText,
          cancelText: options.cancelText,
          onConfirm: options.onConfirm,
          onCancel: options.onCancel,
        },
      ],
    })),

  prompt: (title, options = {}) =>
    set((state) => ({
      messages: [
        ...state.messages,
        {
          id: Date.now(),
          type: 'prompt',
          variant: options.variant || 'info',
          title,
          content: options.content,
          defaultValue: options.defaultValue,
          placeholder: options.placeholder,
          confirmText: options.confirmText,
          cancelText: options.cancelText,
          onConfirm: options.onConfirm,
          onCancel: options.onCancel,
        },
      ],
    })),
}));